import { Button } from '@/components/design-system/Button';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { FormInput } from '@/features/form/inputs/FormInput';
import { cn } from '@/utils/cn';
import { CircleX, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface EmailRequestFormValues {
  email: string;
}

interface EmailRequestFormProps {
  onSubmit: (data: EmailRequestFormValues) => Promise<boolean>;
  loading?: boolean;
  className?: string;
  initialEmail?: string;
  error?: AuthError | null;
}

export const EmailRequestForm = ({ onSubmit, loading, className, initialEmail, error }: EmailRequestFormProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit, watch } = useForm<EmailRequestFormValues>({
    defaultValues: {
      email: initialEmail ?? ''
    }
  });

  const emailValue = watch('email');
  const emailError = error?.type === 'email';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col items-center', className)}>
      <FormInput
        control={control}
        name="email"
        leftIcon={Mail}
        rightIcon={emailError ? CircleX : undefined}
        status={emailError ? 'error' : 'default'}
        placeholder="auth.login.emailPlaceholder"
        autoComplete="email"
        className="w-full"
        hideErrorMessage
      />

      <Button
        type="submit"
        loading={loading}
        disabled={!emailValue || loading}
        className={cn(
          'mt-4 h-10 w-81 border text-base transition-all duration-200',
          loading && 'cursor-wait opacity-60',
          !emailValue || loading
            ? 'cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400'
            : 'bg-primary-500 border-primary-500 hover:bg-primary-500/90 text-white hover:text-white/90'
        )}
      >
        {t('passwordReset.resetButton')}
      </Button>
    </form>
  );
};
